import React, { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import styles from './Assistant.module.scss';

const TONE_ICON = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
};

/**
 * Shell for the pages Claude / Town link to: theme plus a single centred card.
 * They are short-lived — the user arrives from a chat, does one thing, and
 * goes back — so there is no page header or navigation of their own.
 */
const AssistantPage = ({ children }) => {
  const { isDarkMode } = useTheme();
  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: { mode: isDarkMode ? 'dark' : 'light', primary: { main: '#e36250' } },
        typography: { fontFamily: '"Poppins", "sans-serif"' },
      }),
    [isDarkMode]
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <Box className={styles.layout}>
        <Box className={styles.card}>{children}</Box>
      </Box>
    </ThemeProvider>
  );
};

/**
 * One outcome: `tone` is 'loading' | 'success' | 'error' | 'info'.
 * `actions` renders under the text (buttons).
 */
export const AssistantResult = ({ tone, title, hint, actions }) => {
  const Icon = TONE_ICON[tone];
  return (
    <Box className={styles.result}>
      {tone === 'loading' ? (
        <CircularProgress size={40} />
      ) : (
        <span className={`${styles.icon} ${styles[tone]}`}>
          <Icon size={30} />
        </span>
      )}
      <Typography variant="h6" className={styles.title}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="body2" className={styles.hint}>
          {hint}
        </Typography>
      )}
      {actions && <Box className={styles.actions}>{actions}</Box>}
    </Box>
  );
};

export default AssistantPage;
